import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React, { useCallback, useContext, useEffect } from 'react'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { createOptions } from 'utils/commonUtils'
import {
  DestinationTypes,
  QnStatesProps,
  FuelTypes,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import { fail, success } from 'utils/status'
import { initialQnEditState } from '../../../../../states/QnState'
import { getDomain, isKsDomain, isSaficDomain } from 'utils/domain'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import InformationModal from 'components/elements/modal/InformationModal'
import { transitionWithAction } from 'utils/commonUtils'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import { useHistory } from 'react-router-dom'
import DOMAIN from 'constants/domains'
import { CLIENT_SETTINGS } from 'constants/clientSettings'

const fuelTypeOptions = createOptions(VEHICLE_STATE_FORM.fuelType.options)

/**
 * QN-03-f: 燃料切れ
 */
const EmptyFuel: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const {
    destinationType,
    fuelType,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const isDisabled = React.useMemo(() => {
    if (
      fuelType === FuelTypes.electricity &&
      destinationType === DestinationTypes.specify
    ) {
      if (!editState.destinationLocation.isSelected) {
        return true
      }
      return addressMatchError
    }
    return false
  }, [
    editState.vehicleStateInfo,
    editState.destinationLocation,
    addressMatchError,
  ])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (fuelType === FuelTypes.electricity) {
      switch (destinationType) {
        case DestinationTypes.specify:
          return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
        case DestinationTypes.introduction:
          if (
            // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
            isKsDomain()
          ) {
            return ''
          }
          return WARNING.INTRODUCTION
      }
    }
    return ''
  }, [destinationType, fuelType])

  /** 燃料切れ画面モーダルの表示メッセージ */
  const MODAL_MESSAGE = CLIENT_SETTINGS.messages.modal.fuel

  /** 燃料切れ画面モーダルの表示フラグ */
  const [isOpenModal, setIsOpenModal] = React.useState<boolean>(false)

  /** モーダルを閉じて次ページへ遷移を行う */
  const closeModal = React.useCallback(() => {
    setIsOpenModal(false)
    transitionPage(forwardTo)
  }, [])

  /** React routerの遷移オブジェクト */
  const history = useHistory()

  /** 操作履歴のコンテキスト */
  const historyStackContext = useContext(HistoryStackContext)

  /** ページ遷移を行うコールバック */
  const transitionPage = useCallback((nextPagePath: string): void => {
    transitionWithAction(historyStackContext, 'OK', () => {
      history.push(nextPagePath)
    })
  }, [])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  const handleFuelTypeChanged = React.useCallback(
    (value: FuelTypes) => {
      setVehicleStateInfo({
        fuelType: value,
      })
    },
    [setVehicleStateInfo]
  )

  const isOpenModalCase = (option: FuelTypes): boolean => {
    const openModalFuelTypes = ['regular', 'highOctane', 'lightOil']
    return openModalFuelTypes.includes(option)
  }

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    // ガソリン（レギュラー）、ガソリン（ハイオク）、軽油を選択している場合は決定ボタン押下時に遷移せずモーダルを表示する
    if (isOpenModalCase(fuelType)) {
      setIsOpenModal(true)
      return fail()
    }
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      fuelType: fuelType === '' ? FuelTypes.regular : fuelType,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  return (
    <RequestTemplate
      title={'燃料切れ'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.fuelType.formName}の選択`}
            isRequired={true}
            options={fuelTypeOptions}
            selectedValue={fuelType}
            onValueChanged={handleFuelTypeChanged}
          />
          {fuelType === FuelTypes.electricity && (
            <>
              {isKsDomain() ? (
                <KsSelectVehicleModificationContent />
              ) : (
                <SelectVehicleModificationContent />
              )}
              {isKsDomain() ? (
                <KsSelectDestinationContent isIntroduction={true} />
              ) : (
                <SelectDestinationContent />
              )}
            </>
          )}
          {
            // 決定ボタン押下時にモーダルを表示
            <InformationModal
              on={isOpenModal}
              content={MODAL_MESSAGE}
              greyBack
              textCenter
              closeLabel="OK"
              closeFunction={closeModal}
            />
          }
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      notClearEditingItem
      isDisabled={isDisabled}
    />
  )
}

export default EmptyFuel
